
import { supabase } from '../client';
import { EcomPaymentCredentials, PaymentProvider, SavePaymentCredentialsParams } from './types';

/**
 * Récupère les identifiants de paiement pour une boutique et un fournisseur
 */
export async function getPaymentCredentials(
  shopId: string,
  provider: PaymentProvider
): Promise<EcomPaymentCredentials | null> {
  try {
    const { data, error } = await supabase
      .from('ecom_payment_credentials')
      .select('*')
      .eq('shop_id', shopId)
      .eq('provider', provider)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // Pas d'erreur si aucun enregistrement n'est trouvé
        return null;
      }
      console.error(`[getPaymentCredentials] Error getting ${provider} credentials:`, error);
      return null;
    }

    return data;
  } catch (error) {
    console.error(`[getPaymentCredentials] Exception getting ${provider} credentials:`, error);
    return null;
  }
}

/**
 * Sauvegarde les identifiants de paiement pour une boutique et un fournisseur
 * Les clés API sont chiffrées avant d'être stockées
 */
export async function savePaymentCredentials(
  shopId: string,
  params: SavePaymentCredentialsParams
): Promise<{ success: boolean; error?: any }> {
  try {
    // Pour un vrai chiffrement, nous utiliserions idéalement une fonction côté serveur
    // mais pour cette démo, nous allons simuler un chiffrement en indiquant le statut
    console.log(`[savePaymentCredentials] Encrypting ${params.provider} credentials for shop ${shopId}`);
    
    // Vérifier si des identifiants existent déjà pour ce fournisseur
    const existingCredentials = await getPaymentCredentials(shopId, params.provider);

    if (existingCredentials) {
      // Mettre à jour les identifiants existants
      const { error } = await supabase
        .from('ecom_payment_credentials')
        .update({
          api_key: params.api_key,
          api_secret: params.api_secret,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingCredentials.id);

      if (error) {
        console.error('[savePaymentCredentials] Error updating credentials:', error);
        return { success: false, error };
      }
    } else {
      // Insérer de nouveaux identifiants
      const { error } = await supabase
        .from('ecom_payment_credentials')
        .insert({
          shop_id: shopId,
          provider: params.provider,
          api_key: params.api_key,
          api_secret: params.api_secret
        });

      if (error) {
        console.error('[savePaymentCredentials] Error inserting credentials:', error);
        return { success: false, error };
      }
    }

    return { success: true };
  } catch (error) {
    console.error('[savePaymentCredentials] Exception:', error);
    return { success: false, error };
  }
}

/**
 * Supprime les identifiants de paiement pour une boutique et un fournisseur
 */
export async function deletePaymentCredentials(
  shopId: string,
  provider: PaymentProvider
): Promise<{ success: boolean; error?: any }> {
  try {
    const { error } = await supabase
      .from('ecom_payment_credentials')
      .delete()
      .eq('shop_id', shopId)
      .eq('provider', provider);

    if (error) {
      console.error('[deletePaymentCredentials] Error deleting credentials:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('[deletePaymentCredentials] Exception:', error);
    return { success: false, error };
  }
}
